/* eslint-disable @typescript-eslint/no-empty-function */
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Cell, Pie, PieChart } from 'recharts'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { DashboardCard, P } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { IWidget } from 'types/dashboard.interface'
import { WidgetEditOverlay } from 'pages/Dashboard/Dashboard.styled'
import { RootState } from 'store'
import { IHrAttendanceSummary } from 'types/hrDashboard.interface'

const COLORS = {
  present: '#22c55e',
  offDay: '#9ca3af',
  other: '#0ea5e9',
  absent: '#ef4444',
  leave: '#f97316',
  vacation: '#eab308',
  sick: '#a855f7',
} as const

type AttendanceLegendRow = {
  key: string
  label: string
  color: string
  value: number
}

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const DetailsLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colorOrange};
  cursor: pointer;

  &:disabled {
    cursor: inherit;
    opacity: 0.85;
  }
`

const Body = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  flex: 1;
  min-height: 0;
`

const ChartWrap = styled.div`
  position: relative;
  width: 132px;
  height: 132px;
  flex-shrink: 0;
  align-self: center;
`

const CenterLabel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
`

const CenterCount = styled.span`
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.1;
  color: ${COLORS.present};
`

const CenterSub = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.greyText};
  margin-top: 2px;
`

const LegendCol = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  padding: 4px 0 4px 4px;
`

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`

const LegendLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const Bullet = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 1px;
  flex-shrink: 0;
  background: ${(p) => p.$color};
`

const LegendLabel = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.greyText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LegendRight = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-shrink: 0;
`

const LegendValue = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textColor};
  min-width: 1.5rem;
  text-align: right;
`

const LegendPct = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colorLightGray};
  min-width: 2rem;
  text-align: right;
`

function buildRows(summary: IHrAttendanceSummary | null): AttendanceLegendRow[] {
  const s = summary
  const other =
    (s?.excused ?? 0) +
    (s?.holidayIn ?? 0) +
    (s?.holidayOut ?? 0) +
    (s?.training ?? 0) +
    (s?.maternityLeave ?? 0) +
    (s?.unassigned ?? 0)

  return [
    { key: 'present', label: 'Present', color: COLORS.present, value: s?.present ?? 0 },
    { key: 'offDay', label: 'Off Day', color: COLORS.offDay, value: s?.offDay ?? 0 },
    { key: 'other', label: 'Other', color: COLORS.other, value: other },
    { key: 'absent', label: 'Absent', color: COLORS.absent, value: s?.absent ?? 0 },
    { key: 'leave', label: 'Leave', color: COLORS.leave, value: s?.leave ?? 0 },
    { key: 'vacation', label: 'Vacation', color: COLORS.vacation, value: s?.vacation ?? 0 },
    { key: 'sick', label: 'Sick', color: COLORS.sick, value: s?.sickLeave ?? 0 },
  ]
}

export const AttendanceWidget = ({
  modalOpen,
  removeWidget,
  onDragOver,
  onDragStart,
  onDragDrop,
}: IWidget) => {
  const { attendanceSummary } = useSelector((state: RootState) => state.hrDashboard)
  const { darkMode } = useSelector((state: RootState) => state.globals)
  const navigate = useNavigate()

  const rows = useMemo(() => buildRows(attendanceSummary), [attendanceSummary])

  const total = useMemo(() => rows.reduce((acc, r) => acc + r.value, 0), [rows])

  const pieData = useMemo(() => {
    if (total <= 0) {
      const emptyRing = darkMode ? '#6b7280' : '#9ca3af'
      return [{ name: 'empty', value: 1, color: emptyRing }]
    }
    return rows
      .filter((r) => r.value > 0)
      .map((r) => ({ name: r.key, value: r.value, color: r.color }))
  }, [rows, total, darkMode])

  const presentCount = attendanceSummary?.present ?? 0

  return (
    <DashboardCard
      draggable={modalOpen}
      onDragStartCapture={() => {
        onDragStart('attendance', 1)
      }}
      onDrop={onDragDrop}
      onDragEnd={(e) => {
        e.preventDefault()
      }}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver('attendance')
      }}
      width='1fr'
      style={{
        gridArea: 'attendance',
        cursor: modalOpen ? 'grab' : 'normal',
      }}
    >
      {modalOpen && (
        <WidgetEditOverlay
          onClick={(e) => {
            removeWidget('attendance')
            e.stopPropagation()
          }}
        >
          <P>Remove Widget</P>
          <CloseIcon />
        </WidgetEditOverlay>
      )}
      <CardHeader>
        <TitleText weight='bold'>ATTENDANCE</TitleText>
        <DetailsLink
          type='button'
          disabled={modalOpen}
          onClick={(e) => {
            e.stopPropagation()
            if (!modalOpen) {
              navigate('/hr/attendance')
            }
          }}
        >
          Details
          <span aria-hidden='true'>{'>'}</span>
        </DetailsLink>
      </CardHeader>
      <Body>
        <ChartWrap>
          <PieChart width={132} height={132} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={pieData}
              dataKey='value'
              cx='50%'
              cy='50%'
              innerRadius={40}
              outerRadius={58}
              startAngle={90}
              endAngle={-270}
              paddingAngle={total > 0 ? 1 : 0}
              stroke='none'
              isAnimationActive={false}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${entry.name}-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
          <CenterLabel>
            <CenterCount>{presentCount}</CenterCount>
            <CenterSub>PRESENT</CenterSub>
          </CenterLabel>
        </ChartWrap>
        <LegendCol>
          {rows.map((row) => {
            const pct = total > 0 ? Math.round((row.value / total) * 100) : 0
            return (
              <LegendRow key={row.key}>
                <LegendLeft>
                  <Bullet $color={row.color} />
                  <LegendLabel>{row.label}</LegendLabel>
                </LegendLeft>
                <LegendRight>
                  <LegendValue>{row.value}</LegendValue>
                  <LegendPct>{pct}%</LegendPct>
                </LegendRight>
              </LegendRow>
            )
          })}
        </LegendCol>
      </Body>
    </DashboardCard>
  )
}
